import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import ConfirmModal from '../components/ConfirmModal';
import { DSButton, DSPageHeader } from '../design-system/primitives';
import StatusBadge from '../components/StatusBadge';
import './SquareCatalogConfig.css';

function SquareCatalogConfig() {
  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmState, setConfirmState] = useState(null);

  useEffect(() => {
    loadCatalogItems();
  }, []);

  const loadCatalogItems = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get('/square/catalog/items');
      setCatalogItems(response.data);
    } catch (err) {
      setError('Failed to load catalog items');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscoverCatalog = () => {
    setConfirmState({
      title: 'Discover Catalog Items?',
      message: 'This will fetch all membership items from Square Catalog. Continue?',
      confirmLabel: 'Continue',
      onConfirm: async () => {
        try {
          setDiscovering(true);
          setError('');
          setSuccess('');

          const response = await apiService.post('/square/catalog/discover');
          setSuccess(`Found ${response.data.itemsFound} membership items`);
          setConfirmState(null);

          // Reload catalog items
          await loadCatalogItems();
        } catch (err) {
          const errorMsg = err.response?.data?.error || 'Failed to discover catalog items';
          setError(errorMsg);
          console.error(err);
        } finally {
          setDiscovering(false);
        }
      }
    });
  };

  const handleToggleActive = async (itemId, currentActive) => {
    try {
      setError('');
      setSuccess('');
      
      await apiService.patch(`/square/catalog/items/${itemId}`, {
        isActive: !currentActive
      });
      
      setSuccess('Catalog item updated');
      
      // Update local state
      setCatalogItems(items =>
        items.map(item =>
          item.id === itemId ? { ...item, is_active: !currentActive } : item
        )
      );
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to update catalog item';
      setError(errorMsg);
      console.error(err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="square-catalog-page">
        <div className="loading">Loading catalog items...</div>
      </div>
    );
  }

  return (
    <div className="square-catalog-page">
      <DSPageHeader
        title="Square Catalog Configuration"
        actions={
          <DSButton
            appearance="solid"
            tone="primary"
            onClick={handleDiscoverCatalog}
            disabled={discovering}
          >
            {discovering ? 'Discovering...' : 'Discover Catalog Items'}
          </DSButton>
        }
      />

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="catalog-info">
        <p>
          These are the membership items from your Square Catalog that will be tracked
          when processing payments. Only active items are used to identify membership
          payments in orders and invoices.
        </p>
      </div>

      {catalogItems.length === 0 ? (
        <div className="no-items">
          <p>No catalog items found. Click "Discover Catalog Items" to fetch from Square.</p>
        </div>
      ) : (
        <table className="catalog-table">
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Price</th>
              <th>Catalog ID</th>
              <th>Status</th>
              <th>Last Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {catalogItems.map(item => (
              <tr key={item.id} className={item.is_active ? '' : 'inactive'}>
                <td>
                  <strong>{item.item_name}</strong>
                  {item.variation_name && (
                    <span className="variation-name"> - {item.variation_name}</span>
                  )}
                </td>
                <td>{formatCurrency(item.price_amount)}</td>
                <td>
                  <code className="catalog-id">{item.square_catalog_id}</code>
                </td>
                <td>
                  <StatusBadge variant={item.is_active ? 'success' : 'error'} uppercase>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </StatusBadge>
                </td>
                <td className="date">{formatDate(item.updated_at)}</td>
                <td>
                  <button
                    className={`btn-toggle ${item.is_active ? 'deactivate' : 'activate'}`}
                    onClick={() => handleToggleActive(item.id, item.is_active)}
                  >
                    {item.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="catalog-footer">
        <p>
          <strong>Note:</strong> Deactivating an item will prevent it from being used to
          identify membership payments in future syncs, but won't affect existing payment records.
        </p>
      </div>

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={discovering}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}

export default SquareCatalogConfig;
